/**
 * FPD Stats Page
 * 
 * This page is ONLY visible to DigniFi organization users.
 * It displays detailed FPD statistics by month, portfolio, and other dimensions.
 */

import { useState } from "react";
import DashboardLayout from "@/components/DashboardLayout";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { BarChart3, TrendingUp, TrendingDown, Calendar, Briefcase, DollarSign, AlertTriangle, Loader2 } from "lucide-react";
import { useOrganizationContext } from "@/hooks/useOrganization";
import { trpc } from "@/lib/trpc";
import DateRangeFilter, { DateRange } from "@/components/DateRangeFilter";
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  LineChart,
  Line,
  Legend,
  ComposedChart,
  Area,
} from "recharts";

// Color palette
const COLORS = {
  primary: "#3b82f6",
  success: "#22c55e",
  warning: "#f59e0b",
  danger: "#ef4444",
  muted: "#6b7280",
  purple: "#8b5cf6",
};

export default function FPDStats() {
  const { organizationName, isLoading: orgLoading } = useOrganizationContext();
  
  // Date range filter state
  const [dateRange, setDateRange] = useState<DateRange>({});
  
  // Fetch available date range
  const { data: availableDates } = trpc.dignifi.availableDateRange.useQuery();
  
  // Fetch filtered data from API
  const { data: fpdSummary, isLoading: summaryLoading } = trpc.dignifi.fpdSummaryFiltered.useQuery(dateRange);
  const { data: fpdByMonth, isLoading: monthLoading } = trpc.dignifi.fpdByMonthFiltered.useQuery(dateRange);
  const { data: fpdByPortfolio, isLoading: portfolioLoading } = trpc.dignifi.fpdByPortfolioFiltered.useQuery(dateRange);
  const { data: projectedImpact, isLoading: impactLoading } = trpc.dignifi.projectedImpactFiltered.useQuery(dateRange);

  const isLoading = orgLoading || summaryLoading || monthLoading || portfolioLoading || impactLoading;

  if (isLoading) {
    return (
      <DashboardLayout>
        <div className="flex items-center justify-center h-64">
          <div className="flex flex-col items-center gap-4">
            <Loader2 className="h-8 w-8 animate-spin text-primary" />
            <p className="text-muted-foreground">Loading FPD statistics...</p>
          </div>
        </div>
      </DashboardLayout>
    );
  }

  // Transform monthly data for chart
  const monthlyChartData = fpdByMonth?.map(item => ({
    month: item.month,
    total: Number(item.total_accounts),
    fpd: Number(item.fpd_count || 0),
    fpdRate: Number(item.fpd_rate || 0),
    principal: Number(item.total_principal || 0),
    fpdPrincipal: Number(item.fpd_principal || 0),
  })) || [];

  // Transform portfolio data for chart
  const portfolioChartData = fpdByPortfolio?.map(item => ({
    name: item.portfolio,
    total: Number(item.total_accounts),
    fpd: Number(item.fpd_count || 0),
    fpdRate: Number(item.fpd_rate || 0),
    principal: Number(item.total_principal || 0),
  })) || [];

  // Calculate totals
  const totalAccounts = Number(fpdSummary?.total_accounts || 0);
  const totalFPD = Number(fpdSummary?.fpd_count || 0);
  const totalPrincipal = Number(fpdSummary?.total_principal || 0);
  const fpdPrincipal = Number(fpdSummary?.fpd_principal || 0);
  const avgFPDRate = Number(fpdSummary?.fpd_rate || 0);

  // Projected impact
  const defaultsPrevented = Number(projectedImpact?.defaults_prevented || 0);
  const principalProtected = Number(projectedImpact?.principal_protected || 0);

  // Format date range display
  const getDateRangeLabel = () => {
    if (!dateRange.startDate && !dateRange.endDate) return null;
    const formatMonth = (m: string) => {
      const [year, month] = String(m).split('-');
      return new Date(parseInt(year), parseInt(month) - 1).toLocaleDateString('en-US', { month: 'short', year: 'numeric' });
    };
    if (dateRange.startDate && dateRange.endDate) {
      return `${formatMonth(dateRange.startDate)} - ${formatMonth(dateRange.endDate)}`;
    }
    if (dateRange.startDate) return `From ${formatMonth(dateRange.startDate)}`;
    if (dateRange.endDate) return `Until ${formatMonth(dateRange.endDate)}`;
    return null;
  };

  return (
    <DashboardLayout>
      <div className="space-y-6">
        {/* Header with Date Filter */}
        <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
          <div>
            <h1 className="text-3xl font-bold tracking-tight">FPD Statistics</h1>
            <p className="text-muted-foreground mt-1">
              Detailed First Payment Default Breakdown
              {getDateRangeLabel() && <span className="text-primary ml-2">({getDateRangeLabel()})</span>}
            </p>
          </div>
          <DateRangeFilter
            value={dateRange}
            onChange={setDateRange}
            minDate={availableDates?.min_month}
            maxDate={availableDates?.max_month}
          />
        </div>

        {/* Key Metrics */}
        <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-4">
          <Card>
            <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
              <CardTitle className="text-sm font-medium">Total Accounts</CardTitle>
              <BarChart3 className="h-4 w-4 text-muted-foreground" />
            </CardHeader>
            <CardContent>
              <div className="text-2xl font-bold">{totalAccounts.toLocaleString()}</div>
              <p className="text-xs text-muted-foreground">
                In FPD analysis dataset
              </p>
            </CardContent>
          </Card>

          <Card>
            <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
              <CardTitle className="text-sm font-medium">Total Principal</CardTitle>
              <DollarSign className="h-4 w-4 text-blue-500" />
            </CardHeader>
            <CardContent>
              <div className="text-2xl font-bold text-blue-500">${totalPrincipal.toLocaleString()}</div>
              <p className="text-xs text-muted-foreground">
                Across all accounts
              </p>
            </CardContent>
          </Card>

          <Card>
            <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
              <CardTitle className="text-sm font-medium">FPD Principal</CardTitle>
              <AlertTriangle className="h-4 w-4 text-red-500" />
            </CardHeader>
            <CardContent>
              <div className="text-2xl font-bold text-red-500">${fpdPrincipal.toLocaleString()}</div>
              <p className="text-xs text-muted-foreground">
                At risk from defaults
              </p>
            </CardContent>
          </Card>

          <Card>
            <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
              <CardTitle className="text-sm font-medium">Protected (Projected)</CardTitle>
              <TrendingDown className="h-4 w-4 text-green-500" />
            </CardHeader>
            <CardContent>
              <div className="text-2xl font-bold text-green-500">${principalProtected.toLocaleString()}</div>
              <p className="text-xs text-muted-foreground">
                With 10% FPD reduction
              </p>
            </CardContent>
          </Card>
        </div>

        {/* Monthly Trend Chart */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Calendar className="h-5 w-5" />
              FPD Trend by Month
            </CardTitle>
            <CardDescription>Monthly FPD rate and account volume</CardDescription>
          </CardHeader>
          <CardContent>
            <div className="h-[350px]">
              <ResponsiveContainer width="100%" height="100%">
                <ComposedChart data={monthlyChartData}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#333" />
                  <XAxis dataKey="month" stroke="#888" tick={{ fontSize: 11 }} />
                  <YAxis yAxisId="left" stroke="#888" />
                  <YAxis yAxisId="right" orientation="right" stroke="#888" unit="%" />
                  <Tooltip 
                    contentStyle={{ backgroundColor: '#1a1a1a', border: '1px solid #333' }}
                    formatter={(value: number, name: string) => {
                      if (name === 'FPD Rate') return [`${value}%`, name];
                      if (name === 'Principal' || name === 'FPD Principal') return [`$${value.toLocaleString()}`, name];
                      return [value.toLocaleString(), name];
                    }}
                  />
                  <Legend />
                  <Bar yAxisId="left" dataKey="total" fill={COLORS.primary} name="Total Accounts" />
                  <Bar yAxisId="left" dataKey="fpd" fill={COLORS.danger} name="FPD Count" />
                  <Line yAxisId="right" type="monotone" dataKey="fpdRate" stroke={COLORS.warning} name="FPD Rate" strokeWidth={2} dot={{ fill: COLORS.warning }} />
                </ComposedChart>
              </ResponsiveContainer>
            </div>
          </CardContent>
        </Card>

        {/* Portfolio Breakdown */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Briefcase className="h-5 w-5" />
              FPD by Portfolio
            </CardTitle>
            <CardDescription>Performance breakdown by loan portfolio</CardDescription>
          </CardHeader>
          <CardContent>
            <div className="h-[300px]">
              <ResponsiveContainer width="100%" height="100%">
                <BarChart data={portfolioChartData} layout="vertical">
                  <CartesianGrid strokeDasharray="3 3" stroke="#333" />
                  <XAxis type="number" stroke="#888" />
                  <YAxis dataKey="name" type="category" stroke="#888" width={100} tick={{ fontSize: 11 }} />
                  <Tooltip 
                    contentStyle={{ backgroundColor: '#1a1a1a', border: '1px solid #333' }}
                    formatter={(value: number, name: string) => {
                      if (name === 'FPD Rate') return [`${value}%`, name];
                      return [value.toLocaleString(), name];
                    }}
                  />
                  <Legend />
                  <Bar dataKey="total" fill={COLORS.primary} name="Total Accounts" />
                  <Bar dataKey="fpd" fill={COLORS.danger} name="FPD Count" />
                </BarChart>
              </ResponsiveContainer>
            </div>
          </CardContent>
        </Card>

        {/* Monthly Data Table */}
        <Card>
          <CardHeader>
            <CardTitle>Monthly FPD Breakdown</CardTitle>
            <CardDescription>Detailed statistics by first due month</CardDescription>
          </CardHeader>
          <CardContent>
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead>
                  <tr className="border-b border-border">
                    <th className="text-left py-3 px-4 font-medium">Month</th>
                    <th className="text-right py-3 px-4 font-medium">Total Accounts</th>
                    <th className="text-right py-3 px-4 font-medium">FPD Count</th>
                    <th className="text-right py-3 px-4 font-medium">FPD Rate</th>
                    <th className="text-right py-3 px-4 font-medium">Total Principal</th>
                    <th className="text-right py-3 px-4 font-medium">FPD Principal</th>
                  </tr>
                </thead>
                <tbody>
                  {monthlyChartData.map((row, index) => (
                    <tr key={index} className="border-b border-border/50 hover:bg-muted/50">
                      <td className="py-3 px-4 font-medium">{row.month}</td>
                      <td className="text-right py-3 px-4">{row.total.toLocaleString()}</td>
                      <td className="text-right py-3 px-4">{row.fpd.toLocaleString()}</td>
                      <td className="text-right py-3 px-4">
                        <span className={row.fpdRate <= 10 ? "text-green-500" : row.fpdRate <= 15 ? "text-yellow-500" : "text-red-500"}>
                          {row.fpdRate}%
                        </span>
                      </td>
                      <td className="text-right py-3 px-4">${row.principal.toLocaleString()}</td>
                      <td className="text-right py-3 px-4 text-red-500">${row.fpdPrincipal.toLocaleString()}</td>
                    </tr>
                  ))}
                </tbody>
                <tfoot>
                  <tr className="border-t-2 border-border font-bold">
                    <td className="py-3 px-4">Total</td>
                    <td className="text-right py-3 px-4">{totalAccounts.toLocaleString()}</td>
                    <td className="text-right py-3 px-4">{totalFPD.toLocaleString()}</td>
                    <td className="text-right py-3 px-4 text-yellow-500">{avgFPDRate}%</td>
                    <td className="text-right py-3 px-4">${totalPrincipal.toLocaleString()}</td>
                    <td className="text-right py-3 px-4 text-red-500">${fpdPrincipal.toLocaleString()}</td>
                  </tr>
                </tfoot>
              </table>
            </div>
          </CardContent>
        </Card>

        {/* Portfolio Data Table */}
        <Card>
          <CardHeader>
            <CardTitle>Portfolio FPD Breakdown</CardTitle>
            <CardDescription>Detailed statistics by portfolio</CardDescription>
          </CardHeader>
          <CardContent>
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead>
                  <tr className="border-b border-border">
                    <th className="text-left py-3 px-4 font-medium">Portfolio</th>
                    <th className="text-right py-3 px-4 font-medium">Total Accounts</th>
                    <th className="text-right py-3 px-4 font-medium">FPD Count</th>
                    <th className="text-right py-3 px-4 font-medium">FPD Rate</th>
                    <th className="text-right py-3 px-4 font-medium">Total Principal</th>
                    <th className="text-right py-3 px-4 font-medium">Risk Level</th>
                  </tr>
                </thead>
                <tbody>
                  {portfolioChartData.map((row, index) => {
                    const riskLevel = row.fpdRate <= 10 ? 'Low' : row.fpdRate <= 15 ? 'Medium' : 'High';
                    return (
                      <tr key={index} className="border-b border-border/50 hover:bg-muted/50">
                        <td className="py-3 px-4 font-medium">{row.name}</td>
                        <td className="text-right py-3 px-4">{row.total.toLocaleString()}</td>
                        <td className="text-right py-3 px-4">{row.fpd.toLocaleString()}</td>
                        <td className="text-right py-3 px-4">
                          <span className={row.fpdRate <= 10 ? "text-green-500" : row.fpdRate <= 15 ? "text-yellow-500" : "text-red-500"}>
                            {row.fpdRate}%
                          </span>
                        </td>
                        <td className="text-right py-3 px-4">${row.principal.toLocaleString()}</td>
                        <td className="text-right py-3 px-4">
                          <span className={`inline-flex items-center px-2 py-1 rounded-full text-xs ${
                            riskLevel === 'Low' 
                              ? 'bg-green-500/20 text-green-500' 
                              : riskLevel === 'Medium'
                              ? 'bg-yellow-500/20 text-yellow-500'
                              : 'bg-red-500/20 text-red-500'
                          }`}>
                            {riskLevel}
                          </span>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          </CardContent>
        </Card>
      </div>
    </DashboardLayout>
  );
}
